import { inject } from '@angular/core';
import {
  CreateLibraryCommand,
  validateCreateLibraryCommand
} from '@model/library/commands/library-create-command';
// eslint-disable-next-line deprecation/deprecation
import { LibraryLegacyAdapter } from '@model/library/mappers/library-legacy-adapter';
import {
  LibraryRepository,
  CreateLibraryResponse,
  CreateLibraryInput,
  RawCreateLibraryInput
} from '@service/laboratory/library/library-repository';
import { EMPTY, Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { WorklistsLocalStorageUsecase } from './worklists-local-storage.usecase';

export class CreateLibraryUseCase {
  private readonly repository = inject(LibraryRepository);
  private readonly worklistLocalStorageUsecase = inject(
    WorklistsLocalStorageUsecase
  );

  /**
   * Execute library creation
   *
   * @param input - Library creation command or legacy request
   * @returns Observable with created library information
   * @throws Error if validation fails
   */
  execute(input: CreateLibraryInput): Observable<CreateLibraryResponse> {
    // Check if it's a batch creation with multiple library groups
    // In this case, pass directly to repository without normalization
    if (this.isBatchCreation(input)) {
      return this.executeBatchCreation(input);
    }

    // Normalize to command
    const command = this.normalizeToCommand(input);

    // Use command validation (validates per technology)
    validateCreateLibraryCommand(command);

    // Extract lgCodes from specimens
    const lgCodesToRemove = command.specimens.map((s) => s.lgCode);

    return this.repository.createLibrary(command).pipe(
      tap(() => {
        // Remove lgCodes from localStorage after creating the library
        // Errors are silently ignored to not affect the main flow
        if (lgCodesToRemove.length > 0) {
          this.worklistLocalStorageUsecase
            .deleteByLgList(lgCodesToRemove)
            .pipe(catchError(() => EMPTY))
            .subscribe();
        }
      })
    );
  }

  /**
   * Checks if the input is a batch creation with multiple library groups
   */
  private isBatchCreation(input: CreateLibraryInput): boolean {
    return this.hasBatchLibraryGroups(input);
  }

  /**
   * Executes batch creation for multiple library groups
   * Passes the input directly to the repository without normalization
   */
  private executeBatchCreation(
    input: CreateLibraryInput
  ): Observable<CreateLibraryResponse> {
    if (!this.hasBatchLibraryGroups(input)) {
      return this.repository.createLibrary(input);
    }

    // Extract all lgCodes from all groups
    const lgCodesToRemove = input.libraryGroups.flatMap((group) =>
      group.samples.map((s) => s.lgCode)
    );

    return this.repository.createLibrary(input).pipe(
      tap(() => {
        // Errors are silently ignored to not affect the main flow
        if (lgCodesToRemove.length > 0) {
          this.worklistLocalStorageUsecase
            .deleteByLgList(lgCodesToRemove)
            .pipe(catchError(() => EMPTY))
            .subscribe();
        }
      })
    );
  }

  /**
   * Normalizes input to a CreateLibraryCommand
   */
  private normalizeToCommand(input: CreateLibraryInput): CreateLibraryCommand {
    if (this.isCreateLibraryCommand(input)) {
      return input;
    }
    // eslint-disable-next-line deprecation/deprecation
    return LibraryLegacyAdapter.toCommand(input);
  }

  private isRawCreateLibraryInput(
    input: CreateLibraryInput
  ): input is RawCreateLibraryInput {
    return 'samples' in input;
  }

  private hasBatchLibraryGroups(
    input: CreateLibraryInput
  ): input is RawCreateLibraryInput & {
    libraryGroups: { samples: { lgCode: string }[] }[];
  } {
    if (!input || typeof input !== 'object') {
      return false;
    }

    const candidate = input as {
      libraryGroups?: unknown;
    };

    if (!Array.isArray(candidate.libraryGroups) || candidate.libraryGroups.length <= 1) {
      return false;
    }

    return (
      candidate.libraryGroups.every(
        (group) =>
          group &&
          typeof group === 'object' &&
          Array.isArray((group as { samples?: unknown }).samples) &&
          (group as { samples: unknown[] }).samples.every(
            (sample) =>
              sample &&
              typeof sample === 'object' &&
              typeof (sample as { lgCode?: unknown }).lgCode === 'string'
          )
      )
    );
  }

  /**
   * Type guard to check if input is already a CreateLibraryCommand
   */
  private isCreateLibraryCommand(
    input: CreateLibraryInput
  ): input is CreateLibraryCommand {
    return 'specimens' in input && Array.isArray(input.specimens);
  }
}
